import { useMemo } from 'react';
import { NavLink, useNavigate, useNavigation } from 'react-router';
import { useTracker, type Tracker } from '@archipro-website/tracker';

import { cn } from '@rocco/utils/cn';
import { PROJECT_BROWSE_VIEWS } from '@modules-rocco/projects/config/project-browse-views';
import type {
    ProjectBrowseView,
    ProjectBrowseViewConfig,
} from '@modules-rocco/projects/config/project-browse-views';
import { LensSwitch } from '~/ui-rocco/components/LensSwitch';
import { SCROLL_TO_PILL_BAR_STATE } from './use-scroll-to-pill-bar';
import type { DirectoryPillBarEntitySwitch } from './DirectoryPillBar';

/**
 * Reports a switch between the Projects / Spaces / Styles views. The switch is
 * a revamp-only control and reported nothing, so which lens readers browse
 * projects through was unknown.
 */
const logProjectViewSwitch = (
    tracker: Tracker,
    { view, label, path }: ProjectBrowseViewConfig,
): void => {
    const payload = { view, label, path };
    void tracker.log('LeftNavCategoryClick', {
        url: new URL(window.location.href),
        data: {
            ...payload,
            Source: 'projectViewPills',
            ExtraData: JSON.stringify(payload),
        },
    });
};

/**
 * The same views as browse modes for the mobile refine sheet, where the pill
 * switch has no room in the row. Reports what the pills report: it is the same
 * switch, made from another control.
 */
export const useProjectViewModes = (
    activeView: ProjectBrowseView,
): DirectoryPillBarEntitySwitch['modes'] => {
    const tracker = useTracker();
    const navigate = useNavigate();

    return useMemo(
        () =>
            PROJECT_BROWSE_VIEWS.map((config) => ({
                label: config.label,
                isActive: config.view === activeView,
                onSelect: () => {
                    logProjectViewSwitch(tracker, config);
                    navigate(config.path);
                },
            })),
        [activeView, navigate, tracker],
    );
};

/**
 * Projects / Spaces / Styles, as one switch in the filter row.
 *
 * The three share a frame, with the current view as a pane that travels between
 * them, the same control the project page's Gallery / Grid toggle is. They are
 * three ways into the same index rather than three filters, so they read as one
 * switch beside the category pills, with the divider separating the two jobs.
 */
export const ProjectViewPills = ({
    activeView,
    isDark,
}: {
    activeView: ProjectBrowseView;
    isDark: boolean;
}) => {
    /*
     * The pane follows the click, not the loader.
     *
     * `activeView` comes from the route, which does not change until the new
     * view's loader resolves — so the pane stood still for the whole round trip
     * and then moved at the same moment the grid swapped underneath it. The
     * pending location is known as soon as the link is clicked.
     */
    const tracker = useTracker();
    const navigation = useNavigation();
    const pendingView = navigation.location
        ? PROJECT_BROWSE_VIEWS.find(
              ({ path }) =>
                  navigation.location!.pathname.replace(/\/+$/, '') ===
                  path.replace(/\/+$/, ''),
          )?.view
        : undefined;

    return (
        <LensSwitch
            ariaLabel="Browse projects by"
            tone={isDark ? 'dark' : 'light'}
            className="shrink-0"
            activeKey={pendingView ?? activeView}
            items={PROJECT_BROWSE_VIEWS.map((config) => ({
                key: config.view,
                render: (isActive: boolean) => (
                    <NavLink
                        to={config.path}
                        onClick={() => logProjectViewSwitch(tracker, config)}
                        // Switching view replaces the listing, so the reader
                        // lands with this row at the top — but only when they
                        // had already scrolled past it. See useScrollToPillBar.
                        state={SCROLL_TO_PILL_BAR_STATE}
                        // The hook needs the reader's real position to decide
                        // that, and the router's reset would have thrown it
                        // away before the hook could read it.
                        preventScrollReset
                        // `/projects` is a prefix of `/projects/spaces`, so without
                        // this NavLink marks Projects current on every view.
                        end
                        // Links to three separate routes, so the current one is
                        // marked as the page the reader is on. They are not tabs:
                        // nothing here is shown or hidden in a panel beside them.
                        aria-current={isActive ? 'page' : undefined}
                        className={cn(
                            'flex h-[2.75rem] items-center justify-center rounded-[1rem] bg-transparent px-[1.5rem] whitespace-nowrap no-underline hover:no-underline',
                            'font-sans text-base transition-colors duration-200',
                            'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden',
                            isActive
                                ? isDark
                                    ? 'text-[#141414]'
                                    : 'text-[#323232]'
                                : isDark
                                  ? 'text-white/70 hover:text-white'
                                  : 'text-[#6b6b6f] hover:text-[#323232]',
                        )}
                    >
                        {config.label}
                    </NavLink>
                ),
            }))}
        />
    );
};
